/** Status / match-type / CSV-result chips shared by the WP3.4 screens (Figma CMS-09, CMS-13). */
import type {
  GrantCsvRowStatus,
  GrantMatchType,
  GrantStatus,
  SessionSongStatus,
  SessionStatus,
} from '@akpl/shared';

import { useT } from '../../i18n';
import { Badge, type BadgeProps } from '../ui/panel';

const GRANT_TONE: Record<GrantStatus, NonNullable<BadgeProps['tone']>> = {
  pending: 'warn',
  redeemed: 'ok',
  expired: 'neutral',
  revoked: 'danger',
};

export function GrantStatusBadge({ status }: { status: GrantStatus }) {
  const { t } = useT();
  return (
    <Badge tone={GRANT_TONE[status]}>{t(`cms:status.${status}` as 'cms:status.pending')}</Badge>
  );
}

const MATCH_TONE: Record<GrantMatchType, NonNullable<BadgeProps['tone']>> = {
  device: 'cyan',
  email: 'neutral',
  voucher: 'pink',
};

export function MatchTypeBadge({ type }: { type: GrantMatchType }) {
  const { t } = useT();
  return (
    <Badge tone={MATCH_TONE[type]}>
      {t(`cms:grants.tabs.${type}` as 'cms:grants.tabs.device')}
    </Badge>
  );
}

const CSV_TONE: Record<GrantCsvRowStatus, NonNullable<BadgeProps['tone']>> = {
  ok: 'ok',
  warning: 'warn',
  error: 'danger',
};

export function CsvResultBadge({
  status,
  issue,
}: {
  status: GrantCsvRowStatus;
  issue: string | null;
}) {
  const { t } = useT();
  const key = issue ?? 'ok';
  return (
    <Badge tone={CSV_TONE[status]}>{t(`cms:grants.issue.${key}` as 'cms:grants.issue.ok')}</Badge>
  );
}

const SESSION_TONE: Record<SessionStatus, NonNullable<BadgeProps['tone']>> = {
  active: 'ok',
  closed: 'neutral',
  expired: 'warn',
};

export function SessionStatusBadge({ status }: { status: SessionStatus }) {
  const { t } = useT();
  return (
    <Badge tone={SESSION_TONE[status]}>{t(`cms:status.${status}` as 'cms:status.active')}</Badge>
  );
}

const SONG_TONE: Record<SessionSongStatus, NonNullable<BadgeProps['tone']>> = {
  queued: 'neutral',
  playing: 'cyan',
  played: 'ok',
  skipped: 'warn',
  removed: 'danger',
};

export function SessionSongStatusBadge({ status }: { status: SessionSongStatus }) {
  const { t } = useT();
  return (
    <Badge tone={SONG_TONE[status]}>
      {t(`cms:sessions.songStatus.${status}` as 'cms:sessions.songStatus.played')}
    </Badge>
  );
}
